import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronDown,
  ChevronRight,
  Check,
  Sparkles,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
} from "lucide-react";
import FilterService, { filterMetadataFieldsForColumns } from "./FilterService";

const FIELD_TYPE_ICON = {
  text: Type,
  number: Hash,
  date: Calendar,
  datetime: Calendar,
  boolean: ToggleLeft,
  foreign_key: List,
  enum: List,
  dropdown: List,
};

function fieldIcon(field) {
  return FIELD_TYPE_ICON[field?.field_type] || Type;
}

/**
 * FilterPicker Component
 * Field picker with popular filters + grouped/nested fields, search, and multi-select
 * Rendered inside FilterBuilder's `.ems-flt-pick-scroll`
 */
function FilterPicker({
  module,
  columns = [],
  onFieldSelect,
  selectedFields = [],
  searchQuery = "",
  multiSelectMode = false,
}) {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedChildGroups, setExpandedChildGroups] = useState({});

  // Hide fields explicitly marked non-filterable (false/0/"0").
  // Leave NULL/undefined alone (some environments store inconsistent values).
  const isExplicitlyNonFilterable = (field) =>
    field?.is_filterable === false ||
    field?.is_filterable === 0 ||
    field?.is_filterable === "0";

  // Load metadata on mount or when module changes
  useEffect(() => {
    if (!module) return;

    const loadMetadata = async () => {
      setLoading(true);
      try {
        const result = await FilterService.getFilterMetadata(module);
        if (result.success) {
          const scopedFields = filterMetadataFieldsForColumns(
            result.fields,
            columns
          );
          const allowedFieldKeys = new Set(
            scopedFields.map((field) => field.field_key).filter(Boolean)
          );
          const keepField = (field) =>
            allowedFieldKeys.has(field?.field_key) &&
            !isExplicitlyNonFilterable(field);

          const sanitized = {
            ...result,
            popular_filters: Array.isArray(result.popular_filters)
              ? result.popular_filters.filter(keepField)
              : [],
            groups: Array.isArray(result.groups)
              ? result.groups.map((g) => ({
                  ...g,
                  fields: Array.isArray(g.fields)
                    ? g.fields.filter(keepField)
                    : [],
                  child_groups: Array.isArray(g.child_groups)
                    ? g.child_groups.map((cg) => ({
                        ...cg,
                        fields: Array.isArray(cg.fields)
                          ? cg.fields.filter(keepField)
                          : [],
                      }))
                    : [],
                }))
              : [],
          };

          setMetadata(sanitized);
          // Expand all groups by default
          const defaultExpanded = {};
          if (sanitized.groups) {
            sanitized.groups.forEach((group) => {
              defaultExpanded[group.filter_group_id] = true;
            });
          }
          setExpandedGroups(defaultExpanded);
        }
      } catch (error) {
        console.error("Error loading filter metadata:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
  }, [module, columns]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Toggle child group expansion
  const toggleChildGroup = (parentId, childId) => {
    const key = `${parentId}-${childId}`;
    setExpandedChildGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Filter fields based on search query
  const filterFields = (fields, query) => {
    if (!query) return fields;
    const lowerQuery = query.toLowerCase();
    return fields.filter(
      (field) =>
        field.label?.toLowerCase().includes(lowerQuery) ||
        field.field_key?.toLowerCase().includes(lowerQuery)
    );
  };

  // Filter groups based on search query
  const filterGroups = (groups, query) => {
    if (!query) return groups;
    const lowerQuery = query.toLowerCase();
    return groups
      .map((group) => {
        const filteredFields = filterFields(group.fields || [], query);
        const filteredChildGroups = (group.child_groups || []).map(
          (childGroup) => ({
            ...childGroup,
            fields: filterFields(childGroup.fields || [], query),
          })
        );

        const groupMatches =
          group.group_label?.toLowerCase().includes(lowerQuery) ||
          group.group_name?.toLowerCase().includes(lowerQuery) ||
          filteredFields.length > 0 ||
          filteredChildGroups.some((cg) => cg.fields.length > 0);

        if (groupMatches) {
          return {
            ...group,
            fields: filteredFields,
            child_groups: filteredChildGroups,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  // Memoized filtered data
  const filteredData = useMemo(() => {
    if (!metadata) return { popularFilters: [], groups: [] };

    return {
      popularFilters: filterFields(metadata.popular_filters || [], searchQuery),
      groups: filterGroups(metadata.groups || [], searchQuery),
    };
  }, [metadata, searchQuery]);

  const handleFieldClick = (field) => {
    if (onFieldSelect) {
      onFieldSelect(field);
    }
  };

  const isFieldSelected = (fieldKey) => selectedFields.includes(fieldKey);

  const renderFieldItem = (field) => {
    const isSelected = isFieldSelected(field.field_key);
    const Ic = fieldIcon(field);
    return (
      <button
        key={field.field_key}
        type="button"
        className={"ems-flt-pick-item" + (isSelected ? " on" : "")}
        onClick={() => handleFieldClick(field)}
      >
        {multiSelectMode && (
          <span className={"ems-flt-pick-check" + (isSelected ? " on" : "")}>
            {isSelected && <Check size={12} strokeWidth={3} />}
          </span>
        )}
        <span className="ems-flt-pick-ic">
          <Ic size={16} />
        </span>
        <span className="ems-flt-pick-label">{field.label || field.field_key}</span>
      </button>
    );
  };

  const renderChildGroup = (childGroup, parentId) => {
    const key = `${parentId}-${childGroup.filter_group_id}`;
    const isExpanded = expandedChildGroups[key] ?? true;
    const hasFields = (childGroup.fields || []).length > 0;

    if (!hasFields) return null;

    return (
      <div className="ems-flt-pick-subgroup" key={childGroup.filter_group_id}>
        <button
          type="button"
          className="ems-flt-pick-subhead"
          onClick={() => toggleChildGroup(parentId, childGroup.filter_group_id)}
        >
          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {childGroup.group_label || childGroup.group_name}
        </button>
        {isExpanded && (
          <div className="ems-flt-pick-list">
            {(childGroup.fields || []).map((field) => renderFieldItem(field))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (group) => {
    const isExpanded = expandedGroups[group.filter_group_id] ?? true;
    const hasFields = (group.fields || []).length > 0;
    const hasChildGroups = (group.child_groups || []).length > 0;

    if (!hasFields && !hasChildGroups) return null;

    const fieldCount =
      (group.fields || []).length +
      (group.child_groups || []).reduce((sum, cg) => sum + (cg.fields || []).length, 0);

    return (
      <div className="ems-flt-pick-group" key={group.filter_group_id}>
        <button
          type="button"
          className="ems-flt-pick-grouphead"
          onClick={() => toggleGroup(group.filter_group_id)}
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {group.group_label || group.group_name}
          <span className="ems-flt-pick-groupcount">{fieldCount}</span>
        </button>
        {isExpanded && (
          <>
            {(group.fields || []).length > 0 && (
              <div className="ems-flt-pick-list">
                {(group.fields || []).map((field) => renderFieldItem(field))}
              </div>
            )}
            {(group.child_groups || []).map((childGroup) =>
              renderChildGroup(childGroup, group.filter_group_id)
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="ems-flt-pick-empty">Loading filters…</div>;
  }

  const isEmpty =
    filteredData.popularFilters.length === 0 && filteredData.groups.length === 0;

  return (
    <>
      {filteredData.popularFilters.length > 0 && (
        <div className="ems-flt-pick-group">
          <div className="ems-flt-pick-grouphead">
            <Sparkles size={13} />
            Popular filters
          </div>
          <div className="ems-flt-pick-list">
            {filteredData.popularFilters.map((field) => renderFieldItem(field))}
          </div>
        </div>
      )}

      {filteredData.groups.map((group) => renderGroup(group))}

      {isEmpty && (
        <div className="ems-flt-pick-empty">
          {searchQuery ? `No fields match "${searchQuery}".` : "No filters available"}
        </div>
      )}
    </>
  );
}

export default FilterPicker;
